import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import { COLORS } from '../../theme';
import NotificationBenefitsBalloon from './NotificationBenefitsBalloon';

const tituloDoEstado = ({ bloqueado, requerInstalacao, somenteAtivarDispositivo }) => {
  if (bloqueado) return 'Notificações bloqueadas';
  if (requerInstalacao) return 'Instale o app para receber notificações';
  if (somenteAtivarDispositivo) return 'Ativar neste dispositivo';
  return 'Ative as notificações';
};

/**
 * Diálogo (apresentacional) do pedido de permissão de notificações.
 *
 * O navegador exige um gesto do usuário para pedir a permissão, então o pedido
 * é sempre este diálogo com botão — nunca `Notification.requestPermission()`
 * automático. O container (`NotificationPermissionPrompt`) decide quando abrir.
 */
const NotificationPermissionDialog = ({
  open,
  bloqueado = false,
  requerInstalacao = false,
  somenteAtivarDispositivo = false,
  tentando = false,
  onFechar,
  onAtivar,
}) => {
  const titulo = tituloDoEstado({ bloqueado, requerInstalacao, somenteAtivarDispositivo });
  const apenasFechar = bloqueado || requerInstalacao;

  return (
    <Dialog open={open} onClose={onFechar} fullWidth maxWidth="xs" aria-labelledby="push-permissao">
      <DialogTitle id="push-permissao" component="div" sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            aria-hidden
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              bgcolor: COLORS.orangeTint,
              color: COLORS.orange,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            {bloqueado ? <NotificationsOffIcon /> : <NotificationsActiveIcon />}
          </Box>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            {titulo}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {bloqueado ? (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            O navegador bloqueou as notificações para este site. Abra as configurações do site
            (ícone de cadeado ao lado do endereço), permita as notificações e tente novamente.
          </Alert>
        ) : requerInstalacao ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No iPhone/iPad, toque em <strong>Compartilhar</strong> e depois em{' '}
            <strong>Adicionar à Tela de Início</strong>. Abra o app instalado e ative as
            notificações.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary">
              {somenteAtivarDispositivo
                ? 'A permissão já foi concedida. Falta apenas inscrever este dispositivo para receber os avisos.'
                : 'A permissão ainda não está liberada neste navegador. Ative para não perder nenhuma novidade do estúdio.'}
            </Typography>

            <NotificationBenefitsBalloon />

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {somenteAtivarDispositivo
                ? 'A inscrição vale apenas para este dispositivo.'
                : 'O navegador vai pedir sua autorização agora. Você pode desativar depois em Configurações.'}
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {apenasFechar ? (
          <Button onClick={onFechar} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Entendi
          </Button>
        ) : (
          <>
            <Button onClick={onFechar} sx={{ textTransform: 'none' }}>
              Agora não
            </Button>
            <Button
              variant="contained"
              onClick={onAtivar}
              disabled={tentando}
              startIcon={<NotificationsActiveIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                minHeight: 44,
                bgcolor: COLORS.orange,
                color: COLORS.white,
                '&:hover': { bgcolor: COLORS.orangeDeep },
              }}
            >
              {tentando
                ? 'Ativando…'
                : somenteAtivarDispositivo
                  ? 'Ativar neste dispositivo'
                  : 'Ativar notificações'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

NotificationPermissionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  /** Navegador já bloqueou o site (`Notification.permission === 'denied'`). */
  bloqueado: PropTypes.bool,
  /** iOS/iPadOS sem o PWA instalado: só é possível ativar depois de instalar. */
  requerInstalacao: PropTypes.bool,
  /** Permissão concedida, mas sem inscrição neste dispositivo. */
  somenteAtivarDispositivo: PropTypes.bool,
  /** Desabilita a ação principal enquanto a inscrição é criada. */
  tentando: PropTypes.bool,
  onFechar: PropTypes.func.isRequired,
  onAtivar: PropTypes.func.isRequired,
};

export default NotificationPermissionDialog;
